import React, { useRef, useState, useEffect, useMemo } from "react";
import { PropTypes } from "prop-types";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import TableStore from "@harbor/elements/utils/table/store";
import useColumns from "@harbor/elements/utils/table/hooks/columns";
import AppHOC from "../../../../generics/AppHOC";
import OverLappingSpinner from "../../../../common/Spinner/OverLappingSpinner";
import SettingsDrawer from "../../../../common/SettingsDrawer";
import NoData from "../../../../common/table/NoData";
import { downloadAsCSVFile } from "../../../../utils/common";
import { useTableSettings } from "../../../../utils/table";
import {
  LOCAL_STORAGE_LABELS,
  REPORT_TABLE_DEFAULT_TGL,
} from "../../../../utils/enums";
import { getTableColumns, setReportsData } from "./tableConfig";
import { useI18n } from "../../../../i18n/useI18n";
import apiService from "../../../../config/api-config";
import { toast as showToast } from "../../../../utils/genericCommon";
import ReportTemplateDrawer from "../ReportTemplates/ReportTemplateDrawer";
import useRowSizes from "@harbor/elements/utils/table/hooks/rowSizes";
const [
  HbrModal,
  HbrButton,
  HbrInput,
  HbrIcon,
  HbrTable,
  HbrSpinner,
  HbrPagination,
] = reactWrapper([
  "hbr-modal",
  "hbr-button",
  "hbr-input",
  "hbr-icon",
  "hbr-table",
  "hbr-spinner",
  "hbr-pagination",
]);
// default sort column and sort order
let defaultSort = [
  ["total_octets", "desc"],
  ["application", "asc"],
];
const tableId = "generated_reports_table";

const Table = (props) => {
  const { tViews } = useI18n("views");
  const { selectedOverlay } = props.globalFilter;
  const [, forceUpdate] = useState(null);
  const [loader, setLoader] = useState(true);
  const [exportLoader, setExportLoader] = useState(false);
  const [reportData, setReportData] = useState();
  const [selectedReport, setSelectedReport] = useState(null);
  const [tableError, setTableError] = useState(null);
  const [alert, setAlert] = useState();
  const refModal = useRef(null);
  const refCancelInput = useRef(null);
  const deleteButtonsRef = useRef({});
  const deleteTriggerRef = useRef(null);
  const nextDeleteRowRef = useRef(null);
  const overlayDismiss = (event) => {
    event.preventDefault();
  };

  const onClickCancel = () => {
    refModal.current.element.hide();
  };

  const onModalHide = (event) => {
    if (event.target !== event.currentTarget) return;

    const rowIndex = nextDeleteRowRef.current;
    const focusTarget =
      rowIndex === null
        ? deleteTriggerRef.current
        : deleteButtonsRef.current[rowIndex] ||
          deleteButtonsRef.current[rowIndex - 1] ||
          deleteButtonsRef.current[0];

    nextDeleteRowRef.current = null;
    focusTarget?.setFocus?.({ preventScroll: true });
    setAlert(<span style={{ display: "none" }} />);
  };

  const initialFocus = (event) => {
    event.preventDefault();
    refCancelInput.current.element.setFocus({ preventScroll: true });
  };

  const showReportTableToast = (message, sentiment, duration) => {
    showToast(message, sentiment, duration, "", {
      style: { marginTop: "50px" },
    });
  };

  const loadReports = async () => {
    try {
      setLoader(true);
      if (selectedOverlay !== null) {
        const reportsResponse = await apiService.getAllReports(selectedOverlay);
        setReportData(reportsResponse.data);
        props.setReportsDataCount(reportsResponse?.data?.length);
      }
      setLoader(false);
    } catch (error) {
      setTableError(error);
      setLoader(false);
    }
  };
  const closeReportDrawer = () => {
    setSelectedReport(null);
  };

  const handleCopyPassKey = (e) => {
    if (e.target.name === "copy") {
      showReportTableToast(tViews("workflowSuccessMessage"), "success", 5000);
    }
  };

  const handleActionClick = (e) => {
    if (e.target.dataset.btnname === "edit") {
      const selectedItem = (Array.isArray(reportData) ? reportData : []).find(
        (item) => item.report_uuid === e.target.dataset.reportid,
      );
      if (selectedItem) {
        setSelectedReport(selectedItem);
      }
    }
    if (e.target.dataset.btnname === "delete") {
      deleteTriggerRef.current = e.target;
      setAlert(
        <HbrModal
          ref={refModal}
          open={true}
          onHbr-initial-focus={initialFocus}
          onHbr-after-hide={onModalHide}
          onHbr-overlay-dismiss={overlayDismiss}
          id="report-delete-modal"
          role="dialog"
          aria-labelledby="report-delete-title"
        >
          <div slot="label">
            <HbrIcon name="alerts-warning-large" sentiment="warning"></HbrIcon>{" "}
            <span id="report-delete-title" role="heading" aria-level={2}>
              {tViews("reporTableDelete")}
            </span>
          </div>
          {`${tViews("reportDeleteAlertMsg")}`}{" "}
          <b className="alert-message">{e.target.dataset.reportname}</b>
          <div slot="footer">
            <HbrButton
              ref={refCancelInput}
              data-testid="cancel-button"
              variant="text"
              style={{ marginRight: "16px" }}
              onClick={onClickCancel}
              name="cancel"
            >
              {tViews("reporTableCancel")}
            </HbrButton>
            <HbrButton
              variant="fill"
              onClick={(e) => onSubmit(e)}
              sentiment="danger"
              id={e.target.dataset.reportid}
              data-rowindex={e.target.dataset.rowindex}
              name="delete"
            >
              {tViews("reporTableDelete")}
            </HbrButton>
          </div>
        </HbrModal>,
      );
    }
  };

  const onSubmit = async (event) => {
    if (event.target.name === "delete") {
      await deleteReport(event.target.id, event.target.dataset.rowindex);
      refModal.current.element.hide();
    } else {
      setAlert(<span style={{ display: "none" }} />);
    }
  };

  const openTableSettings = () => {
    tableStore.setState({ settingsOpen: true });
  };

  const registerDeleteButton = (rowIndex, element) => {
    if (element) deleteButtonsRef.current[rowIndex] = element;
  };

  const deleteReport = async (reportId, rowIndex) => {
    if (reportId === null) return;

    try {
      await apiService.deleteReport(reportId);
      const reportUpdated = (
        Array.isArray(reportData) ? reportData : []
      ).filter((report) => report.report_uuid !== reportId);

      deleteTriggerRef.current = null;
      nextDeleteRowRef.current = Number(rowIndex);
      setReportData(reportUpdated);
      props.setReportsDataCount(reportUpdated.length);
      showReportTableToast(tViews("reportDeleteSuccessMsg"), "success", 5000);
    } catch (error) {
      setTableError(error);
    }
  };

  useEffect(() => {
    loadReports();
  }, [selectedOverlay]);

  const columnsHook = useColumns({
    config: () => {
      return getTableColumns(
        handleActionClick,
        handleCopyPassKey,
        openTableSettings,
        registerDeleteButton,
      );
    },
    order: defaultSort,
  });

  const tableStore = useMemo(() => {
    const rowSizesHook = useRowSizes({
      contentHeight: 60,
    });
    const store = new TableStore({
      data: reportData,
      query: { page: 0, pageLength: 30 },
      hooks: [columnsHook, rowSizesHook],
      size: "spacious",
    });
    useTableSettings(
      LOCAL_STORAGE_LABELS.REPORT_TABLE,
      REPORT_TABLE_DEFAULT_TGL,
      columnsHook,
      store,
    );
    return store;
  }, [reportData]);

  useEffect(() => {
    const unsubscribe = tableStore.subscribe(forceUpdate);
    return () => unsubscribe();
  }, [tableStore]);

  // load table data on search
  const onSearch = ({ target: { value } }) => {
    if (value !== search) tableStore.setQuery({ search: value, page: 0 });
  };

  const { source, columns, query, count, rowDefinitions } =
    tableStore.getState();
  const { search, pageLength, page } = query;

  const getExportData = () => {
    const tableData = setReportsData(reportData);
    const exportColumns = columns
      .slice(0)
      .filter(
        (col) =>
          col.name !== "Actions" &&
          col.prop !== "action" &&
          col.prop !== "actions",
      );
    let filteredData = tableData;
    if (search && search !== "") {
      const searchValue = String(search).toLowerCase();
      const keys = exportColumns.map((column) => column.prop);
      filteredData = tableData.filter((item) =>
        keys.some((key) =>
          String(item?.[key] ?? "")
            .toLowerCase()
            .includes(searchValue),
        ),
      );
    }
    setExportLoader(true);
    const columnTitles = exportColumns.map((item) => `"${item.name}"`);
    const data = [columnTitles.join(",")];
    const columnKeys = exportColumns.map((item) => item.prop);
    for (const item of filteredData) {
      const row = [];
      for (const key of columnKeys) row.push(`"${item?.[key] ?? ""}"`);
      data.push(row.join(","));
    }
    downloadAsCSVFile(data.join("\n"), tViews("reportsTitle"));
    setExportLoader(false);
  };

  return (
    <div className="table-container hbr-css__layout-col-md">
      {alert}
      {selectedReport && (
        <ReportTemplateDrawer
          report={selectedReport}
          onClose={closeReportDrawer}
          onGenerated={loadReports}
        />
      )}
      {loader === true && <OverLappingSpinner />}
      <div className="hbr-css__layout-col-md">
        <div className="hbr-css__layout-row-xs hbr-css__layout-justify-between">
          <div className="hbr-css__layout-row-xs">
            <HbrInput
              placeholder={tViews("c360.tSearch")}
              clearable
              disabled={loader}
              value={search}
              onHbr-clear={onSearch}
              onkeydown={(event) => {
                if (event.code === "Enter") onSearch(event);
              }}
              onHbr-blur={onSearch}
            >
              <HbrIcon
                slot="prefix"
                name="magnifying-glass"
                sentiment="neutral"
              />
            </HbrInput>
          </div>
          {Number.isFinite(count) && count > 0 && (
            <HbrButton
              variant="outline"
              onClick={getExportData}
              disabled={loader || exportLoader}
              data-testid="hbr-export-button"
            >
              {exportLoader === true ? (
                <HbrSpinner slot="prefix" />
              ) : (
                <HbrIcon slot="prefix" name="upload-simple" />
              )}
              {tViews("export")}
            </HbrButton>
          )}
        </div>
      </div>
      <HbrTable
        rowDefinitions={rowDefinitions}
        id={tableId}
        data-testid={tableId}
        columns={columns}
        readonly
        headerSlot
        resize
        className="report-hbr-table"
        frameSize={source.length}
        serverSide
        externalSorting
        canFocus={false}
        useClipboard={false}
        onHbr-before-sort={(event) => {
          const { column, order } = event.detail;
          const columnsHook = tableStore.getHook("columns");
          if (order) columnsHook.sort({ order: [[column.id, order]] });
          else if (column.id === defaultSort[0][0])
            columnsHook.sort({ order: [[column.id, "asc"]] });
          else columnsHook.sort({ order: defaultSort });
        }}
        source={source}
      >
        <NoData
          count={count}
          loader={loader}
          error={tableError}
          text={tViews("c360.tEmpty")}
        />
      </HbrTable>
      <HbrPagination
        orderArray={[1, 2, 0]}
        showAll
        disabled={loader}
        tableStore={tableStore}
        pageSize={pageLength}
        itemCount={count}
        aria-label={tViews("a11yLabels.paginationAriaLabel")}
        page={page}
        size="small"
        onHbr-page-changed={(event) => {
          const index = Number.isInteger(event.detail) ? event.detail : 0;
          tableStore.setQuery({ page: index });
        }}
        onHbr-size-changed={(event) => {
          tableStore.setQuery({ pageLength: event.detail.pageSize, page: 0 });
        }}
      />
      <SettingsDrawer
        tableStore={tableStore}
        localStorageName={LOCAL_STORAGE_LABELS.REPORT_TABLE}
        defaultToggles={REPORT_TABLE_DEFAULT_TGL}
        dataTestid={tableId}
      />
    </div>
  );
};

Table.propTypes = {
  globalFilter: PropTypes.object.isRequired,
  data: PropTypes.object,
  setReportsDataCount: PropTypes.func.isRequired,
  actions: PropTypes.object,
  loader: PropTypes.bool,
};

export default AppHOC(Table);
